import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, ChevronDown, ArrowUpRight } from "lucide-react";
import { Reveal, PageHero, CTASection, usePageMeta } from "../components/ui-bits";
import { IMG, imgUrl, projects as staticProjects, projectFilters } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectCard = ({ p, open, onToggle }) => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 16 }}
    transition={{ duration: 0.35 }}
    className="bg-white border border-slate-200 card-lift flex flex-col group"
    data-testid={`project-card-${p.id}`}
  >
    <div className="relative h-52 overflow-hidden">
      <img src={imgUrl(p.img)} alt={p.title} loading="lazy" className="w-full h-full object-cover img-industrial transition-transform duration-700 group-hover:scale-[1.06]" />
      <span className={`absolute top-4 left-4 font-mono-tech text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 ${p.status.includes("Ongoing") ? "bg-[#C5221F] text-white" : "bg-white/95 text-slate-800"}`} data-testid={`project-status-${p.id}`}>
        {p.status}
      </span>
    </div>
    <div className="p-6 flex flex-col flex-1">
      <p className="font-mono-tech text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C5221F]">{p.client}</p>
      <Link to={`/projects/${p.id}`} data-testid={`project-link-${p.id}`} className="group/title">
        <h3 className="mt-2.5 font-display text-base lg:text-lg font-bold text-slate-900 leading-snug group-hover/title:text-[#C5221F] transition-colors duration-200">{p.title}</h3>
      </Link>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500"><MapPin size={12} className="text-slate-400" />{p.location}</p>
      <p className="mt-3 text-[13px] leading-relaxed text-slate-600 line-clamp-3">{p.scope}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {p.categories.map((c) => (
          <span key={c} className="font-mono-tech text-[9px] font-semibold uppercase tracking-widest border border-slate-200 text-slate-500 px-2.5 py-1">{c}</span>
        ))}
      </div>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-96 mt-5" : "max-h-0"}`}>
        <dl className="border border-slate-200">
          {p.specs.map(([k, v], i) => (
            <div key={k} className={`px-4 py-2.5 ${i > 0 ? "border-t border-slate-200" : ""} ${i % 2 === 0 ? "bg-slate-50" : ""}`}>
              <dt className="font-mono-tech text-[9px] font-bold uppercase tracking-widest text-slate-400">{k}</dt>
              <dd className="text-xs font-semibold text-slate-800 mt-0.5">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="mt-5 flex items-center justify-between border-t border-slate-200 pt-4 gap-4">
        <button
          onClick={onToggle}
          data-testid={`project-details-toggle-${p.id}`}
          className="flex items-center gap-2 text-[11px] font-mono-tech font-bold uppercase tracking-widest text-slate-600 hover:text-[#C5221F] transition-colors duration-200"
        >
          {open ? "Hide quick specs" : "Quick specs"}
          <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
        <Link
          to={`/projects/${p.id}`}
          data-testid={`project-full-link-${p.id}`}
          className="flex items-center gap-1.5 text-[11px] font-mono-tech font-bold uppercase tracking-widest text-[#C5221F] hover:text-[#A81B18] transition-colors duration-200"
        >
          Full Details <ArrowUpRight size={13} />
        </Link>
      </div>
    </div>
  </motion.article>
);

const Projects = () => {
  usePageMeta(
    "Projects — Refinery, Petrochemical & EPC Portfolio | G. R. Engineering",
    "Selected projects: IOCL Panipat 900 MT fractionator, HMEL Bathinda site-fabricated towers to 2,230 MT, BPCL Kochi & HMEL reactor–regenerator packages, OPaL Dahej C4 EPC, GAIL mounded storage."
  );
  const [filter, setFilter] = useState("All");
  const [openId, setOpenId] = useState(null);
  const [items, setItems] = useState(staticProjects);
  useEffect(() => {
    axios.get(`${API}/projects`).then((r) => {
      if (Array.isArray(r.data) && r.data.length) setItems(r.data);
    }).catch(() => {});
  }, []);
  const filtered = useMemo(
    () => (filter === "All" ? items : items.filter((p) => p.categories.includes(filter))),
    [filter, items]
  );
  return (
    <>
      <PageHero
        img={IMG("bcpl-fractionator")}
        eyebrow="Projects"
        title="A portfolio written in steel"
        sub="Selected refinery, petrochemical, storage and EPC / LSTK projects — every figure below is from delivered or in-progress work."
      />
      <section className="bg-slate-50 min-h-screen" data-testid="projects-portfolio">
        <div className="container-gre py-14 lg:py-20">
          <div className="flex flex-wrap gap-2" data-testid="project-filters">
            {projectFilters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`project-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-5 py-2.5 font-mono-tech text-[11px] font-bold uppercase tracking-widest border transition-colors duration-200 ${
                  filter === f ? "bg-[#C5221F] border-[#C5221F] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <p className="mt-6 font-mono-tech text-[11px] uppercase tracking-widest text-slate-500" data-testid="project-count">
            Showing {filtered.length} of {items.length} selected projects
          </p>
          <motion.div layout className="mt-8 grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filtered.map((p) => (
                <ProjectCard key={p.id} p={p} open={openId === p.id} onToggle={() => setOpenId(openId === p.id ? null : p.id)} />
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
      <CTASection />
    </>
  );
};

export default Projects;
